/**
 * Async-Kundenauswahl — GET /kunden/options + Hydration GET /kunden/:id
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import api from "../../constants/api.js";
import { getRsStyles } from "../forms/shared/SelectStyles.js";

function labelFromDetail(k) {
  if (!k?.id) return "";
  const firma = (k.firma || "").trim();
  const person = [k.vorname, k.nachname].filter(Boolean).join(" ").trim();
  const ort = (k.ort || "").trim();
  const core = firma && person ? `${firma} – ${person}` : firma || person || "";
  const withOrt = core && ort ? `${core} (${ort})` : core || ort;
  return withOrt.trim() || `Kunde #${k.id}`;
}

function mapOption(row) {
  const id = Number(row.id);
  const label = row.label || row.name || labelFromDetail(row);
  return { value: id, label: String(label) };
}

export default function KundeSelectAsync({
  value,
  onChange,
  inputId,
  disabled = false,
  placeholder = "Kunde suchen…",
  isClearable = true,
  menuPlacement = "auto",
  "aria-invalid": ariaInvalid,
  onBlur,
}) {
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [defaults, setDefaults] = useState([]);
  const debounceRef = useRef(null);
  const rsStyles = useMemo(() => getRsStyles(true), []);
  const menuPortalTarget = useMemo(() => (typeof document !== "undefined" ? document.body : undefined), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/kunden/options", { params: { limit: 50 } });
        if (!alive) return;
        const rows = Array.isArray(data) ? data : [];
        const mapped = rows.map(mapOption);
        setDefaults(mapped);
      } catch {
        if (alive) {
          setDefaults([]);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const idNum = value != null && value !== "" ? Number(value) : null;
  const validId = Number.isFinite(idNum) && idNum > 0 ? idNum : null;

  useEffect(() => {
    if (!validId) {
      setSelected(null);
      setLoadError("");
      return;
    }
    let alive = true;
    setLoadError("");
    (async () => {
      try {
        const { data } = await api.get(`/kunden/${validId}`);
        if (!alive) return;
        setSelected(mapOption({ ...data, label: labelFromDetail(data) }));
      } catch {
        if (!alive) return;
        setSelected({ value: validId, label: `Kunde #${validId}` });
        setLoadError("Kunde konnte nicht geladen werden.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [validId]);

  const loadOptions = useCallback((inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const q = String(inputValue || "").trim();
        const { data } = await api.get("/kunden/options", {
          params: { q: q || undefined, limit: q ? 80 : 50 },
        });
        const rows = Array.isArray(data) ? data : [];
        callback(rows.map(mapOption));
      } catch {
        callback([]);
      }
    }, 280);
  }, []);

  const handleChange = useCallback(
    (opt) => {
      setSelected(opt || null);
      onChange?.(opt && Number.isFinite(Number(opt.value)) ? Number(opt.value) : null);
    },
    [onChange],
  );

  return (
    <div className="space-y-1 min-w-0">
      <AsyncSelect
        inputId={inputId}
        instanceId={inputId || "kunde-async"}
        cacheOptions
        defaultOptions={defaults}
        loadOptions={loadOptions}
        value={selected}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={disabled}
        isClearable={isClearable}
        isSearchable
        loadingMessage={() => "Suche…"}
        noOptionsMessage={({ inputValue }) =>
          inputValue && String(inputValue).trim() ? "Keine Treffer." : "Tippen zum Suchen…"
        }
        styles={rsStyles}
        menuPosition="fixed"
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        menuShouldBlockScroll
        classNamePrefix="rs"
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        components={{ IndicatorSeparator: () => null }}
      />
      {loadError ? <p className="text-xs text-amber-400/90">{loadError}</p> : null}
    </div>
  );
}
